import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { CrashBetMode, CrashGamePhase, CrashGameState, CrashRoundPlayer } from '@/types/crash'
import type { RootState } from '@/store/store'

export const initialCrashState: CrashGameState = {
	phase: 'idle',
	crashGameId: null,
	currencyCode: 'USD',
	betMode: 'manual',
	betAmount: '1',
	autoCashoutEnabled: false,
	autoCashoutMultiplier: '2.00',
	autoBetEnabled: false,
	hasBet: false,
	scheduledBet: false,
	hasCashedOut: false,
	currentMultiplier: 1,
	crashPoint: 0,
	winAmount: 0,
	elapsedSeconds: 0,
	bettingWindowSeconds: 0,
	playerCount: 0,
	totalBetAmount: 0,
	players: [],
	history: [],
	isSubmitting: false,
}

const crashSlice = createSlice({
	name: 'crash',
	initialState: initialCrashState,
	reducers: {
		resetCrashGame(state) {
			return {
				...initialCrashState,
				currencyCode: state.currencyCode,
				betAmount: state.betAmount,
				autoCashoutMultiplier: state.autoCashoutMultiplier,
				betMode: state.betMode,
				autoBetEnabled: state.autoBetEnabled,
				autoCashoutEnabled: state.autoCashoutEnabled,
			}
		},
		setCrashCurrencyCode(state, action: PayloadAction<string>) {
			state.currencyCode = action.payload
		},
		setCrashPhase(state, action: PayloadAction<CrashGamePhase>) {
			state.phase = action.payload
		},
		setCrashGameId(state, action: PayloadAction<string | null>) {
			state.crashGameId = action.payload
		},
		setCrashBetMode(state, action: PayloadAction<CrashBetMode>) {
			state.betMode = action.payload
			state.autoBetEnabled = action.payload === 'auto'
		},
		setCrashBetAmount(state, action: PayloadAction<string>) {
			if (state.hasBet && !state.hasCashedOut) {
				return
			}

			state.betAmount = action.payload
		},
		setCrashAutoCashoutEnabled(state, action: PayloadAction<boolean>) {
			state.autoCashoutEnabled = action.payload
		},
		setCrashAutoCashoutMultiplier(state, action: PayloadAction<string>) {
			state.autoCashoutMultiplier = action.payload
		},
		setCrashScheduledBet(state, action: PayloadAction<boolean>) {
			state.scheduledBet = action.payload
		},
		setCrashMultiplier(state, action: PayloadAction<number>) {
			state.currentMultiplier = action.payload
		},
		setCrashElapsedSeconds(state, action: PayloadAction<number>) {
			state.elapsedSeconds = action.payload
		},
		setCrashHistory(state, action: PayloadAction<number[]>) {
			state.history = action.payload
		},
		setCrashSubmitting(state, action: PayloadAction<boolean>) {
			state.isSubmitting = action.payload
		},
		setCrashRoundStats(state, action: PayloadAction<{ playerCount?: number; totalBetAmount?: number }>) {
			state.playerCount = action.payload.playerCount
			state.totalBetAmount = action.payload.totalBetAmount
		},
		setCrashRoundPlayers(state, action: PayloadAction<CrashRoundPlayer[]>) {
			state.players = action.payload
		},
		upsertCrashRoundPlayer(state, action: PayloadAction<CrashRoundPlayer>) {
			const index = state.players.findIndex((player) => player.playerId === action.payload.playerId)

			if (index >= 0) {
				state.players[index] = action.payload
			} else {
				state.players.unshift(action.payload)
			}
		},
		applyCrashRoundWaiting(state, action: PayloadAction<{ crashGameId?: string | null; bettingWindowSeconds?: number }>) {
			state.phase = 'waiting'
			state.currentMultiplier = 1
			state.crashPoint = 0
			state.elapsedSeconds = 0
			state.hasBet = false
			state.hasCashedOut = false
			state.winAmount = 0
			state.isSubmitting = false
			state.players = []
			state.playerCount = 0
			state.totalBetAmount = 0
			state.bettingWindowSeconds = action.payload.bettingWindowSeconds ?? 0

			if (action.payload.crashGameId !== undefined) {
				state.crashGameId = action.payload.crashGameId
			}
		},
		applyCrashRoundFlying(state) {
			state.phase = 'flying'
			state.currentMultiplier = 1
			state.elapsedSeconds = 0
			state.hasCashedOut = false
			state.winAmount = 0
			state.bettingWindowSeconds = 0

			if (state.autoBetEnabled && !state.hasBet) {
				state.scheduledBet = true
			}
		},
		applyCrashRoundCrashed(state, action: PayloadAction<number>) {
			state.phase = 'crashed'
			state.crashPoint = action.payload
			state.currentMultiplier = 1
			state.bettingWindowSeconds = 0

			if (!state.hasCashedOut && state.hasBet) {
				state.winAmount = 0
			}

			state.hasBet = false
			state.history = [...state.history, action.payload].slice(-20)
		},
		applyCrashBetPlaced(state, action: PayloadAction<{ betAmount: number }>) {
			state.hasBet = true
			state.scheduledBet = false
			state.isSubmitting = false
		},
		applyCrashBetCancelled(state) {
			state.hasBet = false
			state.scheduledBet = false
			state.isSubmitting = false
		},
		applyCrashCashOut(state, action: PayloadAction<{ winAmount: number; cashoutMultiplier?: number }>) {
			state.hasCashedOut = true
			state.winAmount = action.payload.winAmount
			state.isSubmitting = false
		},
	},
})

export const {
	resetCrashGame,
	setCrashCurrencyCode,
	setCrashPhase,
	setCrashGameId,
	setCrashBetMode,
	setCrashBetAmount,
	setCrashAutoCashoutEnabled,
	setCrashAutoCashoutMultiplier,
	setCrashScheduledBet,
	setCrashMultiplier,
	setCrashElapsedSeconds,
	setCrashHistory,
	setCrashSubmitting,
	setCrashRoundStats,
	setCrashRoundPlayers,
	upsertCrashRoundPlayer,
	applyCrashRoundWaiting,
	applyCrashRoundFlying,
	applyCrashRoundCrashed,
	applyCrashBetPlaced,
	applyCrashBetCancelled,
	applyCrashCashOut,
} = crashSlice.actions

export const selectCrashGame = (state: RootState) => state.crash
export const selectCrashPlayers = (state: RootState) => state.crash.players
export const selectCrashHistory = (state: RootState) => state.crash.history
export const selectCrashPhase = (state: RootState) => state.crash.phase

export default crashSlice.reducer
